import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { AlertTriangle, Trash2, Info } from 'lucide-react';

const ConfirmDialogContext = createContext(null);

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState({ open: false, title: '', message: '', confirmLabel: 'Confirm', cancelLabel: 'Cancel', variant: 'danger' });
  const resolveRef = useRef(null);

  const confirm = useCallback(({ title = 'Confirm Action', message = 'Are you sure?', confirmLabel = 'Confirm', cancelLabel = 'Cancel', variant = 'danger' } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, title, message, confirmLabel, cancelLabel, variant });
    });
  }, []);

  const handleConfirm = () => {
    setState(s => ({ ...s, open: false }));
    resolveRef.current?.(true);
    resolveRef.current = null;
  };

  const handleCancel = () => {
    setState(s => ({ ...s, open: false }));
    resolveRef.current?.(false);
    resolveRef.current = null;
  };

  const variantStyles = {
    danger: 'bg-red-600 hover:bg-red-700',
    warning: 'bg-amber-600 hover:bg-amber-700',
    info: 'bg-[#4A7C59] hover:bg-[#3A6346]',
  };

  const variantIcons = {
    danger: <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3"><Trash2 className="w-6 h-6 text-red-500" /></div>,
    warning: <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3"><AlertTriangle className="w-6 h-6 text-amber-500" /></div>,
    info: <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3"><Info className="w-6 h-6 text-emerald-600" /></div>,
  };

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      <Dialog open={state.open} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">{state.title}</DialogTitle>
            <DialogDescription className="text-center pt-2">
              {variantIcons[state.variant] || variantIcons.danger}
              <span>{state.message}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleCancel} className="flex-1">
              {state.cancelLabel}
            </Button>
            <Button onClick={handleConfirm} className={`flex-1 text-white ${variantStyles[state.variant] || variantStyles.danger}`}>
              {state.confirmLabel}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  }
  return context.confirm;
}

export default ConfirmDialogContext;
